import { Link } from "react-router-dom";
import { Trophy, AlertTriangle } from "lucide-react";
import { ClubCrest } from "./ClubCrest";

function zoneFor(position, template) {
  if (!template) return null;
  return (template.zones || []).find((z) => (z.positions || []).includes(position)) || null;
}

// Secondary stats: P/W/D/L visible from sm; TPF/TPA/TPD (Team Points For/Against/Difference) from md.
// Mobile keeps the essentials readable: POS · TEAM · PTS.
const SECONDARY = [
  { key: "played", head: "P", cls: "text-slate-300", bp: "sm" },
  { key: "won", head: "W", cls: "text-emerald-400", bp: "sm" },
  { key: "drawn", head: "D", cls: "text-slate-400", bp: "sm" },
  { key: "lost", head: "L", cls: "text-rose-400", bp: "sm" },
  { key: "tpf", head: "TPF", cls: "text-slate-300", bp: "md" },
  { key: "tpa", head: "TPA", cls: "text-slate-300", bp: "md" },
  { key: "tpd", head: "TPD", cls: "text-slate-300", bp: "md" },
];
const bpCls = (bp) => (bp === "sm" ? "hidden sm:table-cell" : "hidden md:table-cell");

export function LeagueTable({ standings, template }) {
  if (!standings || standings.length === 0)
    return <div className="text-slate-500 text-sm font-mono py-10 text-center">No standings yet.</div>;
  return (
    <div>
      <div className="rounded-xl border border-[#2b2512] overflow-hidden">
        <table className="w-full text-sm" data-testid="league-table">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-slate-500 font-mono bg-[#070707]">
              <th scope="col" className="text-left px-3 py-2 w-10">POS</th>
              <th scope="col" className="text-left px-2 py-2">Team</th>
              {SECONDARY.map((c) => (
                <th key={c.key} scope="col" className={`text-center px-2 py-2 w-12 ${bpCls(c.bp)}`}>{c.head}</th>
              ))}
              <th scope="col" className="text-center px-3 py-2 w-14 text-[#E5B842]">PTS</th>
            </tr>
          </thead>
          <tbody>
            {standings.map((r) => {
              const zone = zoneFor(r.position, template);
              return (
                <tr key={r.team_id} data-testid={`standings-row-${r.position}`} className="border-t border-[#2b2512]/60 hover:bg-[#161616]/60" style={{ transition: "background-color 0.15s ease" }}>
                  <td className="px-3 py-2.5 relative">
                    {zone && <span className="absolute left-0 top-0 bottom-0 w-1" style={{ background: zone.color }} title={zone.label} aria-label={zone.label} />}
                    <span className="font-heading font-black text-slate-200">{r.position}</span>
                  </td>
                  <td className="px-2 py-2.5">
                    <Link to={`/club/${r.team_id}`} className="flex items-center gap-2 text-slate-100 hover:text-[#E5B842]" style={{ transition: "color 0.15s ease" }}>
                      <ClubCrest club={r.club} size={26} />
                      <span className="font-semibold truncate">{r.club?.name}</span>
                      {r.playoff_tie && <AlertTriangle className="w-3.5 h-3.5 text-amber-400" title="WSTF Playoff tie flag" />}
                      {zone && <span className="sm:hidden w-1.5 h-1.5 rounded-full shrink-0" style={{ background: zone.color }} aria-label={zone.label} />}
                    </Link>
                  </td>
                  <td className={`text-center px-2 py-2.5 text-slate-300 ${bpCls("sm")}`}>{r.played}</td>
                  <td className={`text-center px-2 py-2.5 text-emerald-400 ${bpCls("sm")}`}>{r.won}</td>
                  <td className={`text-center px-2 py-2.5 text-slate-400 ${bpCls("sm")}`}>{r.drawn}</td>
                  <td className={`text-center px-2 py-2.5 text-rose-400 ${bpCls("sm")}`}>{r.lost}</td>
                  <td className={`text-center px-2 py-2.5 text-slate-300 ${bpCls("md")}`}>{r.tpf}</td>
                  <td className={`text-center px-2 py-2.5 text-slate-300 ${bpCls("md")}`}>{r.tpa}</td>
                  <td className={`text-center px-2 py-2.5 font-mono text-slate-300 ${bpCls("md")}`}>{r.tpd > 0 ? `+${r.tpd}` : r.tpd}</td>
                  <td className="text-center px-3 py-2.5 font-heading font-black text-[#E5B842] text-base">{r.league_points}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="mt-3 text-[11px] font-mono text-slate-600">P Played · W Won · D Drawn · L Lost · TPF Team Points For · TPA Team Points Against · TPD Team Points Difference · PTS League Points</div>
      {template?.zones && (
        <div className="flex flex-wrap gap-4 mt-3" data-testid="zone-legend">
          {template.zones.map((z) => (
            <div key={z.code} className="flex items-center gap-2 text-xs text-slate-400">
              <span className="w-3 h-3 rounded-sm inline-flex items-center justify-center" style={{ background: z.color }}>
                {z.code === "CHAMPION" && <Trophy className="w-2.5 h-2.5 text-[#000000]" />}
              </span>
              {z.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
